import { useEffect, useRef, useState } from 'react';

import { CheckIcon, CopyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';

const COPY_RESET_DELAY = 1500;

interface CopyApiKeyButtonProps {
  apiKey: string;
  onCopy?: () => void;
}

/**
 * Button to copy an API key to clipboard with visual feedback.
 * Shows a copy icon by default, switches to check icon after copying.
 */
export const CopyApiKeyButton = ({ apiKey, onCopy }: CopyApiKeyButtonProps) => {
  const { t } = useTranslation();
  const [showCopyButton, setShowCopyButton] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setShowCopyButton(false);
      onCopy?.();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setShowCopyButton(true);
        timeoutRef.current = null;
      }, COPY_RESET_DELAY);
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <Button
      onClick={handleClick}
      className="mt-3 mb-6 flex h-12 w-full items-center justify-center gap-2 text-base"
    >
      {showCopyButton ? (
        <>
          <CopyIcon className="size-4" />
          {t('developer_tools.api_modal.copy_button')}
        </>
      ) : (
        <>
          <CheckIcon className="size-4" />
          {t('developer_tools.api_modal.copied_button')}
        </>
      )}
    </Button>
  );
};
